import { create } from 'zustand';
import { supabase } from '../lib/supabase';

const CURRENT_SPACE_STORAGE_KEY = 'pairtab-current-space-id';

const persistCurrentSpaceId = (spaceId: string | null) => {
  try {
    if (spaceId) {
      localStorage.setItem(CURRENT_SPACE_STORAGE_KEY, spaceId);
    } else {
      localStorage.removeItem(CURRENT_SPACE_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures and keep in-memory state working.
  }
};

const loadPersistedCurrentSpaceId = () => {
  try {
    return localStorage.getItem(CURRENT_SPACE_STORAGE_KEY);
  } catch {
    return null;
  }
};

interface Space {
  id: string;
  name: string;
  space_type: 'couple' | 'family' | 'roommates' | 'trip';
  currency: string;
  start_date: string;
  invite_token: string;
  is_active: boolean;
}

interface SpaceMember {
  id: string;
  user_id: string;
  display_name: string;
  avatar_emoji: string;
  role: 'owner' | 'member';
  joined_at: string;
}

interface SpaceState {
  currentSpace: Space | null;
  members: SpaceMember[];
  isLoading: boolean;
  isHydrating: boolean;
  setCurrentSpace: (space: Space | null) => void;
  clearCurrentSpace: () => void;
  restoreCurrentSpace: () => Promise<void>;
  createSpace: (space: Partial<Space>, members: { display_name: string; avatar_emoji: string }[]) => Promise<void>;
  fetchSpace: (id: string) => Promise<void>;
  fetchMembers: (spaceId: string) => Promise<void>;
  saveOnboardingMembers: (
    spaceId: string,
    owner: { display_name: string; avatar_emoji: string },
    members: { display_name: string; avatar_emoji: string }[]
  ) => Promise<void>;
  joinSpace: (token: string, display_name: string, avatar_emoji: string) => Promise<void>;
}

export const useSpaceStore = create<SpaceState>((set) => ({
  currentSpace: null,
  members: [],
  isLoading: false,
  isHydrating: true,

  setCurrentSpace: (space) => {
    persistCurrentSpaceId(space?.id ?? null);
    set({ currentSpace: space });
  },

  clearCurrentSpace: () => {
    persistCurrentSpaceId(null);
    set({ currentSpace: null, members: [], isHydrating: false });
  },

  restoreCurrentSpace: async () => {
    set({ isHydrating: true });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        persistCurrentSpaceId(null);
        set({ currentSpace: null, members: [] });
        return;
      }

      const headers = {
        Authorization: `Bearer ${session.access_token}`,
      };

      const persistedSpaceId = loadPersistedCurrentSpaceId();
      const response = persistedSpaceId
        ? await fetch(`/api/spaces/${persistedSpaceId}`, { headers })
        : await fetch('/api/spaces/current', { headers });

      const fallbackResponse = persistedSpaceId && !response.ok
        ? await fetch('/api/spaces/current', { headers })
        : null;

      const finalResponse = fallbackResponse || response;
      const payload = await finalResponse.json().catch(() => ({}));

      if (!finalResponse.ok) {
        if (finalResponse.status === 404) {
          persistCurrentSpaceId(null);
          set({ currentSpace: null, members: [] });
          return;
        }
        throw new Error(payload.error || 'Failed to restore current space');
      }

      persistCurrentSpaceId(payload.id);
      set({ currentSpace: payload });
    } finally {
      set({ isHydrating: false });
    }
  },

  createSpace: async (space, _members) => {
    set({ isLoading: true });
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch('/api/spaces', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          ...space,
          members: _members,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to create space');
      }

      persistCurrentSpaceId(payload.id);
      set({ currentSpace: payload });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchSpace: async (id) => {
    set({ isLoading: true });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`/api/spaces/${id}`, {
        headers: {
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load space');
      }

      persistCurrentSpaceId(payload.id);
      set({ currentSpace: payload });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchMembers: async (spaceId) => {
    const { data, error } = await supabase
      .from('space_members')
      .select('*')
      .eq('space_id', spaceId)
      .is('left_at', null)
      .order('joined_at', { ascending: true });
    
    if (error) throw error;
    set({ members: data || [] });
  },

  saveOnboardingMembers: async (spaceId, owner, members) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`/api/spaces/${spaceId}/members`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({ owner, members }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to save members');
    }
  },

  joinSpace: async (token, display_name, avatar_emoji) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`/api/spaces/join/${token}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({ display_name, avatar_emoji }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to join space');
    }

    if (payload.space_id) {
      persistCurrentSpaceId(payload.space_id);
    }
  }
}));
